import { BaJwt, LoginEventPayload } from '@oiambk/oiam-oauth-types';
import log from 'loglevel';
import { cookieConfig } from '../../assets/cookie-config';
import { OauthStates, OiamCookie } from '../model/constants';
import { ERR_OIAM_MULTIPLE_INSTANCES } from '../model/errors';
import { AdditionalInfo, OiamSessionCookie } from '../model/oiamSessionCookie';
import { LoopBreaker } from './loop-breaker';
import { NginxErrorLogger } from './nginx-error-logger';
import { decodeBase64url, encodeBase64url, generateUUID, getDomainPart } from './utils';

interface InternalStateStorage {
  [key: string]: string;
}

export class StoreService {
  private static readonly KEY_OAUTHSTATE = 'oiam-oauth-wc-state';
  private static readonly KEY_OAUTHINSTANCE = 'oiam-oauth-wc-instance';
  private static readonly KEY_OAUTHFALLBACKINSTANCE = 'oiam-oauth-fallback-instance';
  /**
   * This is the part that should be continued after returning vom Keycloak.
   * One-time actions like forced profile-selection normally are not part of this.
   * Same for accountLink, which only gets copied over from PREV in case of retries.
   */
  private static readonly KEY_OAUTH_PREV_INVOCATION = 'oiam-oauth-wc-prev-invocation';
  /**
   * "retry invocation" is the full configuration of the last login/stepup/kc_action attempt.
   * Should there be the need for a repeated try (e.g. because the previous login was to old),
   * this attempt can be recreated by copying it from "retry invocation" to "previous invocation".
   */
  private static readonly KEY_OAUTH_RETRY_INVOCATION = 'oiam-oauth-wc-retry-invocation';

  private static readonly KEY_BAHF_INAPP = 'bahf_inapp';

  private static instance: string;
  private static clientid: string = '';
  private static authority: string = '';

  private static readonly hostsWithoutOAG: string[] = [
    'secure.dev.ocp.webapp.idst.ibaintern.de',
    'test.dev.ocp.webapp.idst.ibaintern.de',
    'localhost',
  ];

  /** a specific client has been previously invoked, is not transferrable to other clients */
  private static get keyPrevInvocation() {
    return this.KEY_OAUTH_PREV_INVOCATION + ':' + this.clientid;
  }
  private static get keyRetryInvocation() {
    return this.KEY_OAUTH_RETRY_INVOCATION + ':' + this.clientid;
  }

  public static createInstance() {
    const inst = this.loadInstance();
    if (inst.instance) {
      this.instance = inst.instance;
    } else {
      this.instance = generateUUID();
      this.setInstance(this.instance);
    }
    return this.isRunningInstance();
  }

  public static createFallbackInstance() {
    const inst = this.getFallbackInstance();
    if (inst.instance) {
      this.instance = inst.instance;
    } else {
      this.instance = generateUUID();
      this.setFallbackInstance(this.instance);
    }
    return this.isRunningFallbackInstance();
  }

  public static isSameInstance(instance) {
    return this.instance === instance;
  }

  public static readState(): OauthStates {
    const sta = this.getFromSessionStorage(this.KEY_OAUTHSTATE);
    if (sta) {
      const astates = JSON.parse(sta);
      const astate: OauthStates = astates[this.clientid];
      if (astate) {
        return astate;
      }
    }
    return OauthStates.EMPTY;
  }

  public static deleteState() {
    this.removeFromSessionStorage(this.KEY_OAUTHSTATE);
    return this.removeFromSessionStorage(this.KEY_OAUTHSTATE);
  }

  /**
   * Save state per clientid
   * Zentrales Item im sessionStorage damit es einfach gelöscht werden kann
   *
   * @param state
   */
  public static saveState(state: OauthStates) {
    // we need the client id
    if (!this.clientid) {
      return;
    }

    let storedstates: InternalStateStorage = {};
    const st = this.getFromSessionStorage(this.KEY_OAUTHSTATE);

    if (st) {
      const states = this.getStatesObjectHandleOldFormats(st);
      storedstates = states || storedstates;
    }
    storedstates[this.clientid] = state;

    this.setToSessionStorage(this.KEY_OAUTHSTATE, JSON.stringify(storedstates));
  }

  public static setSettings(clientid: string, authority: string) {
    this.clientid = clientid;
    this.authority = authority;
  }

  public static getAuthority() {
    return this.authority;
  }

  public static getClientId() {
    return this.clientid;
  }

  public static setInstance(instance) {
    const obj = {
      instance,
    };
    this.setToSessionStorage(this.KEY_OAUTHINSTANCE, JSON.stringify(obj));
  }

  public static setFallbackInstance(instance) {
    const obj = {
      instance,
    };
    this.setToSessionStorage(this.KEY_OAUTHFALLBACKINSTANCE, JSON.stringify(obj));
  }

  public static getInstance() {
    if (!this.instance) {
      this.createInstance();
    }
    return { instance: this.instance };
  }

  public static getFallbackInstance() {
    const inst = this.getFromSessionStorage(this.KEY_OAUTHFALLBACKINSTANCE);
    if (inst) {
      return JSON.parse(inst);
    }
    return {
      instance: null,
    };
  }

  static getOiamSessionCookie(): OiamSessionCookie {
    const cookie = this.getOiamCookie(OiamCookie.SESSIONCOOKIE);
    return cookie ? JSON.parse(cookie) : undefined;
  }

  static storeOiamSessionCookieKeepAdditionalInfo(
    parts?: Pick<
      OiamSessionCookie,
      'actingOnBehalfOf' | 'actingType' | 'maxstart' | 'idlestart' | 'authnlevel' | 'profileHash'
    >,
  ) {
    const parsedSessionCookie: OiamSessionCookie = this.getOiamSessionCookie();
    this.storeOiamSessionCookie(parsedSessionCookie?.additionalinfo, parts);
  }

  private static storeOiamSessionCookie(
    additionalInfo?: AdditionalInfo,
    parts?: Pick<
      OiamSessionCookie,
      'actingOnBehalfOf' | 'actingType' | 'maxstart' | 'idlestart' | 'authnlevel' | 'profileHash'
    >,
  ) {
    const domain = getDomainPart();
    const body: OiamSessionCookie = {
      instance: this.getInstance().instance,
      clientid: this.clientid,
      additionalinfo: additionalInfo,
      ...parts,
    };
    document.cookie =
      OiamCookie.SESSIONCOOKIE +
      '=' +
      encodeBase64url(JSON.stringify(body)) +
      '; path=/; domain=' +
      domain +
      ';' +
      cookieConfig.samesiteLax +
      ';';

    log.debug('oiam-oauth-wc: oiamsession cookie stored', JSON.stringify(body));
  }

  static updateOiamSessionCookie(updatedParts: Pick<OiamSessionCookie, 'idlestart'>) {
    const parsedSessionCookie: OiamSessionCookie = this.getOiamSessionCookie();
    const staticParts = {
      ...(parsedSessionCookie?.actingOnBehalfOf && { actingOnBehalfOf: parsedSessionCookie.actingOnBehalfOf }),
      ...(parsedSessionCookie?.actingType && { actingType: parsedSessionCookie.actingType }),
      ...(parsedSessionCookie?.maxstart && { maxstart: parsedSessionCookie.maxstart }),
      ...(parsedSessionCookie?.authnlevel && { authnlevel: parsedSessionCookie.authnlevel }),
      ...(parsedSessionCookie?.profileHash && { profileHash: parsedSessionCookie.profileHash }),
      ...updatedParts,
    };
    this.storeOiamSessionCookie(parsedSessionCookie?.additionalinfo, staticParts);
  }

  static setAdditionalInfoInOiamSessionCookie(additionalInfo: AdditionalInfo) {
    const parsedSessionCookie: OiamSessionCookie = this.getOiamSessionCookie();
    const staticParts = {
      ...(parsedSessionCookie?.actingOnBehalfOf && { actingOnBehalfOf: parsedSessionCookie.actingOnBehalfOf }),
      ...(parsedSessionCookie?.actingType && { actingType: parsedSessionCookie.actingType }),
      ...(parsedSessionCookie?.maxstart && { maxstart: parsedSessionCookie.maxstart }),
      ...(parsedSessionCookie?.idlestart && { idlestart: parsedSessionCookie.idlestart }),
      ...(parsedSessionCookie?.authnlevel && { authnlevel: parsedSessionCookie.authnlevel }),
      ...(parsedSessionCookie?.profileHash && { profileHash: parsedSessionCookie.profileHash }),
    };
    this.storeOiamSessionCookie(additionalInfo, staticParts);
  }

  static checkForOiamSessionIdCookie() {
    return this.getPlainCookie(OiamCookie.SESSIONID);
  }

  /**
   * There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
   * To prevent errors in that case, store the cookie at client side (login)
   */
  static storeOiamSessionIdCookieOnTestDevOrLocalhost(accessToken: string) {
    if (this.hostsWithoutOAG.indexOf(window.location.hostname) >= 0) {
      const jwt: BaJwt = new BaJwt(accessToken);
      const domain = getDomainPart();
      document.cookie =
        OiamCookie.SESSIONID +
        '=' +
        jwt.payload.sid +
        '; path=/; domain=' +
        domain +
        ';' +
        cookieConfig.samesiteLax +
        ';';
    }
  }

  /**
   * There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
   * To prevent errors in that case, remove the cookie at client side (logout)
   */
  static removeOiamSessionIdCookieOnTestDevOrLocalhost() {
    if (this.hostsWithoutOAG.indexOf(window.location.hostname) >= 0) {
      log.debug('removeOiamSessionIdCookieOnTestDevOrLocalhost', this.hostsWithoutOAG, window.location.hostname);
      this.deleteOiamCookie(OiamCookie.SESSIONID);
    }
  }

  static getOiamCookie(cookiename: string) {
    const plain = this.getPlainCookie(cookiename);
    return decodeBase64url(plain);
  }

  static getPlainCookie(cookiename: string) {
    let name = cookiename + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let cpart of ca) {
      let c = cpart.trimStart();
      if (c.startsWith(name)) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  static deleteOiamCookie(cookiename: string) {
    const domain = getDomainPart();
    document.cookie = cookiename + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;' + ' domain=' + domain + ';';
  }

  public static getPreviousInvocation(): LoginEventPayload {
    const prev = this.getFromSessionStorage(this.keyPrevInvocation);
    return JSON.parse(prev);
  }

  public static getRetryInvocation(): LoginEventPayload {
    const retry = this.getFromSessionStorage(this.keyRetryInvocation);
    return JSON.parse(retry);
  }

  public static setPreviousInvocation(
    c: LoginEventPayload,
    fieldsToExclude: (keyof LoginEventPayload)[] = ['authnLevel', 'accountLink'],
  ): LoginEventPayload {
    const pruned = { ...c };
    // these settings should only be active for the next Keycloak round
    for (const f of fieldsToExclude) {
      delete pruned[f];
    }
    this.setToSessionStorage(this.keyPrevInvocation, JSON.stringify(pruned));
    return pruned;
  }

  public static copyRetryToPreviousInvocation() {
    const retryInv = this.getRetryInvocation();
    // full retry with all options set
    const prevInv = this.setPreviousInvocation(retryInv, []);
    log.debug('oiam-oauth-wc: copy retry invocation as previous', retryInv, prevInv);
  }

  public static setRetryInvocation(c: LoginEventPayload): void {
    this.setToSessionStorage(this.keyRetryInvocation, JSON.stringify(c));
  }

  public static deletePreviousInvocation(): void {
    this.removeFromSessionStorage(this.keyPrevInvocation);
  }

  public static deleteRetryInvocation(): void {
    this.removeFromSessionStorage(this.keyRetryInvocation);
  }

  public static setBahfInApp(value: string) {
    this.setToSessionStorage(this.KEY_BAHF_INAPP, value);
  }

  /**
   * We have to remove any user data from the session storage at logout
   * Currently we do a session storage clear, but this might change in future
   *
   * To perform a logout with the oidc-client-ts, we must remember and restore necessary data (state, id-token ...)
   */
  public static deleteUserData(keepRetryInvocation: boolean, keepLoopBreaker = false) {
    // delete state
    this.removeFromSessionStorage(this.KEY_OAUTHSTATE);

    // first get the data, the oidc-client-ts will need for the logout
    // key format "oidc.user:[authority]:[client-id]"
    const key = 'oidc.user:' + this.authority + ':' + this.clientid;
    const data = this.getFromSessionStorage(key);
    const retryInv = keepRetryInvocation ? this.getRetryInvocation() : undefined;
    const loopBreaker = keepLoopBreaker ? this.getFromSessionStorage(LoopBreaker.STORAGE_KEY) : undefined;

    // now clear storage
    this.clearStorage();

    // restore the oidc-client-ts data
    if (data) {
      this.setToSessionStorage(key, data);
    }
    if (retryInv) {
      this.setRetryInvocation(retryInv);
    }
    if (loopBreaker) {
      this.setToSessionStorage(LoopBreaker.STORAGE_KEY, loopBreaker);
    }
  }

  /**
   * @returns the access Unix timestamp in seconds from the last OAG backend call
   */
  public static getBackendAccessTS() {
    const ts = StoreService.getPlainCookie(OiamCookie.BACKENDACCESSCOOKIE);
    const result = +ts;
    if (isNaN(result)) {
      return 0;
    }
    return result;
  }

  /**
   * Check for multiple instances of oiam-oauth-component
   * Store cache value at window object to prevent multiple intances to render
   *
   * @private
   */
  private static isRunningInstance() {
    const elements = document.getElementsByTagName('oiam-oauth-component');
    if (elements.length > 1) {
      if (window['oiam_running']) {
        // keep console log for eService team to see
        log.error('oiam-oauth-wc: Multiple Instances available -> only one instance will render', 'main');
        NginxErrorLogger.logError({ ...ERR_OIAM_MULTIPLE_INSTANCES, details: 'oiam-oauth-component' });
        return false;
      } else {
        window['oiam_running'] = this.instance;
      }
    }
    return true;
  }

  private static isRunningFallbackInstance() {
    const elements = document.getElementsByTagName('oiam-oauth-fallback');
    if (elements.length > 1) {
      if (window['oiam_fallback_running']) {
        // keep console log for eService team to see
        log.error('oiam-oauth-wc: Multiple Instances available -> only one instance will render', 'fallback');
        NginxErrorLogger.logError({ ...ERR_OIAM_MULTIPLE_INSTANCES, details: 'oiam-oauth-fallback' });
        return false;
      } else {
        window['oiam_fallback_running'] = this.instance;
      }
    }
    return true;
  }

  private static loadInstance() {
    const inst = this.getFromSessionStorage(this.KEY_OAUTHINSTANCE);
    if (inst) {
      return JSON.parse(inst);
    }
    return {
      instance: null,
    };
  }

  /**
   * There are sometimes exceptions at Storage get especially on firefox (NS_ERROR_FILE_NOT_FOUND, NS_ERROR_FILE_CORRUPTED)
   * use try catch to get rid of the nginx error logs in such case
   * we cannot do anything to fix it
   */
  private static getFromSessionStorage(key: string) {
    try {
      return sessionStorage.getItem(key);
    } catch (ex) {
      // it is a problem of the users system
      // we cannot do anything about it
      // just log local
      log.error('Exception at read sessionStorage', key, ex);
    }
    return null;
  }

  private static setToSessionStorage(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    } catch (ex) {
      log.error('Exception at write sessionStorage', key, ex);
    }
  }

  private static removeFromSessionStorage(key: string) {
    try {
      sessionStorage.removeItem(key);
    } catch (ex) {
      log.error('Exception at remove from sessionStorage', key, ex);
    }
  }

  private static clearStorage() {
    try {
      sessionStorage.clear();
    } catch (ex) {
      log.error('Exception at clear Storage', ex);
    }
  }

  private static getStatesObjectHandleOldFormats(statesstr: string) {
    const states = JSON.parse(statesstr);
    if (Object.getOwnPropertyNames(states).includes('internalstate')) {
      console.debug('Deprecated state format found: will be deleted');
      this.deleteState();
    } else {
      return states;
    }
  }
}
